import { useTranslation } from "react-i18next";
import { Heading } from "@/components/atoms/heading";
import { ApplicationCardSimple } from "@/components/molecules/application/application.card.simple";
import { ShiftCardSimple } from "@/components/molecules/shift/shift-card.simple";
import { useRevokePeriod } from "@/lib/backend/utils/hooks/use-revoke-period";
import { useApplications } from "@/lib/query/application/use-applications-for-project";
import { useShiftsForProject } from "@/lib/query/shift/use-shifts-for-project";

type CombinedItem = {
  type: "shift" | "application";
  startAt: string;
  data: any;
};

export function ProjectShifts({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { getRevokePeriodText } = useRevokePeriod();
  const { data: shifts } = useShiftsForProject(projectId);
  const { data: applications } = useApplications(projectId);

  // First, create an array of shift dates for easy comparison
  const shiftDates = shifts?.items?.map((shift) => new Date(shift.startAt).toDateString()) || [];

  // Combine and sort shifts and applications
  const combinedItems: CombinedItem[] = [
    ...(shifts?.items?.map((shift) => ({
      type: "shift" as const,
      startAt: shift.startAt,
      data: shift,
    })) || []),
    // Filter applications to exclude those that have a shift on the same day
    ...(applications?.items
      ?.filter((application) => {
        const applicationDate = new Date(
          application.availabilitySlot?.startAt || "",
        ).toDateString();
        return !shiftDates.includes(applicationDate);
      })
      .map((application) => ({
        type: "application" as const,
        startAt: application.availabilitySlot?.startAt || "",
        data: application,
      })) || []),
  ].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  return (
    <div className="ion-padding flex flex-col gap-4">
      <section>
        <Heading order={2}>{t("common:applications.deadline.title")}</Heading>
        <p className="text-txt-secondary">
          {t("common:applications.deadline.description", {
            revokePeriodText: getRevokePeriodText(),
          })}
        </p>
      </section>

      <section>
        <Heading order={2}>{t("routes:projects.shifts.title")}</Heading>
        <div className="flex flex-col gap-4">
          {!combinedItems.length && (
            <p className="text-txt-secondary">{t("common:shifts.no-shifts")}</p>
          )}
          {combinedItems.map((item) =>
            item.type === "shift" ? (
              <ShiftCardSimple key={`shift-${item.data.id}`} shift={item.data} />
            ) : (
              <ApplicationCardSimple key={`application-${item.data.id}`} application={item.data} />
            ),
          )}
        </div>
      </section>
    </div>
  );
}
